import * as React from "react";
import {useEffect, useState} from "react";
import {IBenchmarkReturn, IPlanTreeNode} from "../../model/Portfolio.model";
import {getDateOptions, getLatestDate, isDcPlan, PerformanceType} from "./portfolioUtil";
import {PerformanceTreeTable} from "./PerformanceTreeTable";
import {NewSelectComponent} from "../common/NewSelect.component";
import {useDispatch, useSelector} from "react-redux";
import {performanceSelectedDate, performanceType} from "./PortfolioMapSelectors";
import {setPerformanceDateAction, setPerformanceTypeAction} from "./PortfolioActions";
import {IAccountRollingPerformance, IUniverseReturn, PortfolioApi} from "../../api/PortfolioApi";
import {QuarterlyPerformance} from "./QuarterlyPerformance";
import {PerformanceSummaryChart} from "./PerformanceSummaryChart";
import {StyledToggle} from "../common/material-ui-core/Toggle.component";
import {formatDateAndTimeCustom} from "../../utils/dateUtil";
import {IApplicationRootState} from "../../../applicationState";
import {AnyThunkDispatch} from "../../../commonTypes";

export interface IPerformanceTabProps {
    planTreeNodes: IPlanTreeNode[];
    planType: string;
    accountRollingPerformance: IAccountRollingPerformance[];
}

export const PORTFOLIO_PERFORMANCE_DISCLOSURE_TEXT = "Source: Investment Metrics Portfolio Analytics and Reporting (PARis). Past performance is not indicative of future results. As with any investment, there is potential for profit as well as the possibility of loss. Client accounts are based on month-end market values and cash flow data provided by each individual client’s custodian provided to Aon. Client account investment performance is calculated on the Investment Metrics PARis system using the Modified Dietz method. Performance analytics for each individual third-party investment strategy or product is sourced from eVestment and may vary or differ from the client performance shown herein. Performance reflects the deduction of fees charged by external investment managers which may be in the form of a management fee and/or fees and expenses applicable to pooled investment vehicles but does not reflect the deduction of Aon’s investment advisory fees. Investment management fees and other investment-related expenses would reduce performance.";
export const PORTFOLIO_SHOW_PEER_GROUP_SLIDER_TEXT = "Show Peer Group and Rankings";
export const PORTFOLIO_PERFORMANCE_FOOTNOTE_TEXT = "Peer Universe rankings are based upon an ordering system where 1 represents the highest ranking and 100\n" +
    "            the lowest ranking. Please note that the composition of the peer universe can change over time, and the\n" +
    "            rankings displayed on this website were calculated as of <<date>>.\n" +
    "            As a result, these rankings may differ from those provided in the quarterly investment report, which may be\n" +
    "            based on data pulled at a different time.";

export const PerformanceTab: React.FunctionComponent<IPerformanceTabProps> = (props) => {
    const dateOptions= getDateOptions(props.planTreeNodes);
    const [benchmarkReturns, setBenchmarkReturns] = useState<IBenchmarkReturn[]>([]);
    const [disclosureOpen, setDisclosureOpen] = useState<boolean>(false);
    const [displayPeerGroupAndRankings, setDisplayPeerGroupAndRankings] = useState<boolean>(false);
    const [universeReturns, setUniverseReturns] = useState<IUniverseReturn[]>([]);

    const dateFromStore = useSelector((state: IApplicationRootState) => performanceSelectedDate(state));
    const performanceTypeFromStore = isDcPlan(props.planType)
        ? useSelector((state: IApplicationRootState) => performanceType(state)) ?? PerformanceType.RELATIVE
        : PerformanceType.ABSOLUTE;
    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        if (!dateFromStore) {
            dispatch(setPerformanceDateAction(dateOptions[0]));
        }
    }, [props.planTreeNodes]);

    useEffect(() => {
        PortfolioApi.getBenchmarkReturns()
            .then((response) => {
                setBenchmarkReturns(response);
            });

        if (isDcPlan(props.planType)) {
            if (!performanceTypeFromStore) {
                dispatch(setPerformanceTypeAction(PerformanceType.RELATIVE));
            }
            PortfolioApi.getUniverseReturns()
                .then(response => {
                    setUniverseReturns(response);
                });
        } else {
            dispatch(setPerformanceTypeAction(PerformanceType.ABSOLUTE));
        }
    }, []);

    const renderPerformanceSummaryChart = () => {
        if (isDcPlan(props.planType)) {
            return null;
        }
        return dateFromStore
            && <PerformanceSummaryChart
                selectedDate={dateFromStore}
                planTreeNodes={props.planTreeNodes}
                benchmarkReturns={benchmarkReturns}
            />;
    };

    const renderPerformanceTreeTable = () => {
        return dateFromStore
            && <PerformanceTreeTable
                selectedDate={dateFromStore}
                datedPlanNodes={props.planTreeNodes}
                performanceType={performanceTypeFromStore}
                benchmarkReturns={benchmarkReturns}
                universeReturns={displayPeerGroupAndRankings ? universeReturns : []}
                isDcPlan={isDcPlan(props.planType)}
            />;
    };

    const handleDateChange = (e: any) => {
        dispatch(setPerformanceDateAction(dateOptions.find((date) => date.id === e.target.value)!));
    };
    const renderDateSelection = () => {
        return <div className="performance-tab__date-selection" data-testid="performance-tab__date-selection">
            <div className="performance-tab__date-label">As of:</div>
            <NewSelectComponent
                id={"performance-tab__dates"}
                values={dateOptions}
                selected={dateFromStore?.id}
                width={200}
                handleChange={handleDateChange}
                classNameSuffix="filter-menu_performance-tab-dates"
            />
        </div>;
    };
    const handlePerformanceTypeChange = (type: PerformanceType) => {
        dispatch(setPerformanceTypeAction(type));
    };

    const renderBenchmarkRule = () => {
        return <div className="performance-tab__benchmark-rule" data-testid="performance-tab__benchmark-rule">
            <p>
                Benchmark Rule:<br/>
                Actively Managed/Blended: Above Benchmark = <span className="green">green</span>, underperformance
                within 1.0% = <span className="yellow">yellow</span>,
                underperformance <span>&gt;</span> 1.0% below = <span className="red">red</span>.<br/>
                Passively Managed: Tracking error within 0.5% = <span className="green">green</span>,
                within 1.0% = <span className="yellow">yellow</span>, <span>&gt;</span> 1.0%
                below = <span className="red">red</span>.<br/>
                Performance is based on the reasonableness of tracking error targets.
            </p>
        </div>;
    };

    const renderDisclosureText = () => {
        return <div className={"performance-tab__disclosure-text"} data-testid="performance-tab__disclosure-text">
            {PORTFOLIO_PERFORMANCE_DISCLOSURE_TEXT}
        </div>;
    };

    const renderDisclosure = () => {
      return <div className={"performance-tab__disclosure-container"}>
          <span className="performance-tab__disclosure-toggle clickable link-16"
                   data-testid="performance-tab__disclosure-toggle"
                   onClick={() => setDisclosureOpen(!disclosureOpen)}>
                    Disclosure
                    <span className={disclosureOpen ? "fal fa-angle-up" : "fal fa-angle-down"}
                          data-testid={"performance-tab__disclosure-icon"}/>
            </span>
            {disclosureOpen && renderDisclosureText()}
        </div>;
    };

    const renderFootnote = () => {
        if (!displayPeerGroupAndRankings
            || performanceTypeFromStore !== PerformanceType.ABSOLUTE
            || universeReturns.length === 0) {
            return null;
        }
        const footerDate =
            formatDateAndTimeCustom(getLatestDate(universeReturns.map(it => it.lastUpdatedTs)), "MMMM DD, YYYY");

        const footNote = PORTFOLIO_PERFORMANCE_FOOTNOTE_TEXT.replace("<<date>>", footerDate);
        return <div className={"performance-tab__footnote"} data-testid="performance-tab__footnote">
            {footNote}
        </div>;
    };

    const renderPeerGroupAndRankingToggle = () => {
        return performanceTypeFromStore === PerformanceType.ABSOLUTE && universeReturns.length > 0
        ? <StyledToggle
            toggleState={displayPeerGroupAndRankings}
            setToggle={() => setDisplayPeerGroupAndRankings(!displayPeerGroupAndRankings)}
            label={PORTFOLIO_SHOW_PEER_GROUP_SLIDER_TEXT}
            labelPlacement="start"/>
        : null;
    };

    const hasAccountRollingPerformanceForSelectedDate = () => {
        if (!dateFromStore || props.accountRollingPerformance.length === 0) {
            return false;
        }
        return props.accountRollingPerformance.some(arp => arp.date === dateFromStore.id);
    };

    const renderRollingButton = () => {
        return hasAccountRollingPerformanceForSelectedDate()
            ? <div
                data-testid="performance-tab__rolling-performance-button"
                className={performanceTypeFromStore === PerformanceType.ROLLING ? "activated"
                    : "clickable performance-tab__rolling-performance-button"}
                onClick={() => handlePerformanceTypeChange(PerformanceType.ROLLING)}>
                Rolling
            </div>
            : null;
    };

    const renderRelativeAbsoluteToggle = () => {
        return isDcPlan(props.planType)
            ? <div className="performance-tab__toggle-container">
                <div className="performance-tab__relative-absolute-toggle" data-testid="performance-tab__relative-absolute-toggle">
                    <div
                        data-testid="performance-tab__relative-performance-button"
                        className={performanceTypeFromStore === PerformanceType.RELATIVE ? "activated" : "clickable"}
                        onClick={() => handlePerformanceTypeChange(PerformanceType.RELATIVE)}
                    >
                        Relative
                    </div>
                    {renderRollingButton()}
                    <div
                        data-testid="performance-tab__absolute-performance-button"
                        className={performanceTypeFromStore === PerformanceType.ABSOLUTE ? "activated" : "clickable"}
                        onClick={() => handlePerformanceTypeChange(PerformanceType.ABSOLUTE)}
                    >
                        Absolute
                    </div>
                </div>
                {renderPeerGroupAndRankingToggle()}
            </div>
            : null;
    };

    return (
        <div className="performance-tab__container" data-testid="performance-tab__container">
            {!isDcPlan(props.planType) && <QuarterlyPerformance/>}
            <div className="performance-tab__table-date-container">
                {renderDateSelection()}
                {renderPerformanceSummaryChart()}
                {renderRelativeAbsoluteToggle()}
                {renderPerformanceTreeTable()}
                {renderFootnote()}
                {performanceTypeFromStore === PerformanceType.RELATIVE && renderBenchmarkRule()}
                {renderDisclosure()}
            </div>
        </div>
    );
};